import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { adminAPI } from "../../api";
import { imgUrl } from "../../api";
import { formatPrecio, formatFecha, STATUS_COLORES, CONTACT_STATUS } from "../../utils";
import toast from "react-hot-toast";
import "./Dashboard.css";

const StatCard = ({ icon, label, value, color, sublabel }) => (
  <div className="stat-card card" style={{ borderTop: `4px solid ${color || "var(--color-accent)"}` }}>
    <div className="stat-card-top">
      <span className="stat-card-icon">{icon}</span>
      <span className="stat-card-value" style={{ color: color || "var(--color-primary)" }}>{value}</span>
    </div>
    <p className="stat-card-label">{label}</p>
    {sublabel && <p className="stat-card-sub">{sublabel}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    adminAPI.dashboard()
      .then((r) => setData(r.data.data))
      .catch(() => toast.error("Error cargando dashboard"))
      .finally(() => setCargando(false));
  }, []);

  const marcarAtendido = async (id) => {
    try {
      await adminAPI.actualizarLead(id, { status: "ATENDIDO" });
      setData((d) => ({
        ...d,
        ultimosLeads: d.ultimosLeads.map((l) =>
          l.id === id ? { ...l, status: "ATENDIDO" } : l
        ),
      }));
      toast.success("Lead marcado como atendido");
    } catch {
      toast.error("Error actualizando lead");
    }
  };

  if (cargando) return <div className="spinner" />;

  const { stats, porTipo, ultimasPropiedades, ultimosLeads } = data || {};

  return (
    <div className="dashboard fade-in">
      <div className="page-header">
        <h1 className="heading-serif page-title">Dashboard</h1>
        <Link to="/admin/properties/new" className="btn btn-accent">
          + Nueva propiedad
        </Link>
      </div>

      {/* ── Stats ── */}
      <div className="stats-grid-admin">
        <StatCard icon="🏠" label="Propiedades activas" value={stats?.activas || 0} color="var(--color-primary)" />
        <StatCard icon="🏷️" label="En venta" value={stats?.en_venta || 0} color="#166534" sublabel="activas" />
        <StatCard icon="🔑" label="En renta" value={stats?.en_renta || 0} color="#1E40AF" sublabel="activas" />
        <StatCard
          icon="📩"
          label="Leads nuevos"
          value={stats?.leads_nuevos || 0}
          color={stats?.leads_nuevos > 0 ? "#DC2626" : "var(--color-text-muted)"}
          sublabel="últimas 48h"
        />
      </div>

      <div className="dashboard-grid">
        {/* ── Últimas propiedades ── */}
        <section className="card dashboard-section">
          <div className="section-header-row">
            <h2 className="section-heading">Últimas propiedades</h2>
            <Link to="/admin/properties" className="btn btn-ghost btn-sm">Ver todas →</Link>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Propiedad</th>
                  <th>Operación</th>
                  <th>Precio</th>
                  <th>Status</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ultimasPropiedades?.map((p) => {
                  const st = STATUS_COLORES[p.status] || {};
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="prop-row">
                          <div className="prop-thumb">
                            {p.thumb ? (
                              <img src={imgUrl(p.thumb)} alt={p.title} />
                            ) : <span>🏠</span>}
                          </div>
                          <div>
                            <p className="prop-row-title">{p.title}</p>
                            <p className="prop-row-city">{p.city}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={p.operation === "VENTA" ? "badge-venta" : "badge-renta"}>
                          {p.operation}
                        </span>
                      </td>
                      <td className="td-price">{formatPrecio(p.price, p.currency, true)}</td>
                      <td>
                        <span className="status-badge" style={{ background: st.bg, color: st.color }}>
                          {st.label || p.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-btns">
                          <Link to={`/admin/properties/${p.id}/edit`} className="btn btn-ghost btn-sm">✏️</Link>
                          <a href={`/propiedades/${p.slug}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">👁️</a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Sidebar: gráfica + leads ── */}
        <div className="dashboard-side">
          {/* Distribución por tipo */}
          {porTipo?.length > 0 && (
            <section className="card dashboard-section">
              <h2 className="section-heading">Por tipo de inmueble</h2>
              <div className="tipo-bars">
                {porTipo.map((t) => {
                  const pct = Math.round((t.count / (stats?.activas || 1)) * 100);
                  return (
                    <div key={t.type} className="tipo-bar-row">
                      <span className="tipo-bar-label">{t.type.replace("_", " ")}</span>
                      <div className="tipo-bar-track">
                        <div className="tipo-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="tipo-bar-count">{t.count}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Últimos leads */}
          <section className="card dashboard-section">
            <div className="section-header-row">
              <h2 className="section-heading">Últimos leads</h2>
              <Link to="/admin/contacts" className="btn btn-ghost btn-sm">Ver todos →</Link>
            </div>
            <div className="leads-list">
              {ultimosLeads?.length === 0 && (
                <p style={{ color: "var(--color-text-muted)", fontSize: ".9rem", padding: "1rem 0" }}>
                  No hay leads aún
                </p>
              )}
              {ultimosLeads?.map((l) => {
                const cs = CONTACT_STATUS[l.status] || {};
                return (
                  <div key={l.id} className="lead-row">
                    <div className="lead-avatar">{l.name.charAt(0).toUpperCase()}</div>
                    <div className="lead-info">
                      <p className="lead-name">{l.name}</p>
                      <p className="lead-prop">{l.property_title || "Contacto general"}</p>
                    </div>
                    <div className="lead-meta">
                      <span className="status-badge" style={{ background: cs.bg, color: cs.color, fontSize: ".7rem" }}>
                        {cs.label || l.status}
                      </span>
                      {l.status === "NUEVO" && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: ".75rem", padding: ".2rem .5rem" }}
                          onClick={() => marcarAtendido(l.id)}
                        >
                          ✓
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
